import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Circle, Loader2, TriangleAlert, Unplug } from 'lucide-react'
import type { Account } from '@limo/database'
import { supabase } from '@/lib/supabase'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { callOttoPortal, relativeTime, type LaPluginStatus } from './ottoPortal'

interface LaStatus {
  connected: boolean
  companyId?: string
  username?: string
  status?: 'active' | 'needs_reauth' | 'disabled'
  verifiedAt?: string | null
}

// Non-2xx from the edge function: dig the JSON body out so a LimoAnywhere
// login rejection (422) reaches the user as words, not a generic failure.
async function invokeOttoPortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('miles-portal', { body })
  if (error) {
    const context = (error as { context?: Response }).context
    const detail = context ? await context.json().catch(() => null) : null
    throw new Error(detail?.message ?? 'unavailable')
  }
  return data as T
}

// LimoAnywhere connects INSIDE Claude: the Otto AI plugin runs on the
// operator's own machine and keeps the login there. The badge therefore
// comes from the plugin's heartbeat for this location (reported through the
// same status call as the rest of the page). The hosted "link a login here"
// form below is the legacy path — only shown for accounts that already used
// it, never offered to new ones.
// adminAccount = impersonation view: status only, no form, no unlink.
export function OttoLaCard({ adminAccount }: { adminAccount?: Account } = {}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { account: ownAccount } = useAuth()
  const account = adminAccount ?? ownAccount
  const [form, setForm] = useState({ companyId: '', username: '', password: '' })
  const [editing, setEditing] = useState(false)

  // Same key as the Status card — React Query shares the response.
  const pluginQuery = useQuery({
    queryKey: ['otto-status', account?.id],
    queryFn: () =>
      callOttoPortal(adminAccount ? { action: 'status', accountId: adminAccount.id } : { action: 'status' }),
    enabled: !!account?.id,
    refetchInterval: 30_000,
    retry: 1,
  })
  const plugin: LaPluginStatus | null | undefined = pluginQuery.data?.laPlugin

  const statusQuery = useQuery({
    queryKey: ['otto-la-status', adminAccount?.id ?? 'self'],
    queryFn: () =>
      invokeOttoPortal<LaStatus>(
        adminAccount ? { action: 'la_status', accountId: adminAccount.id } : { action: 'la_status' },
      ),
    retry: 1,
  })

  const connectMutation = useMutation({
    mutationFn: () => invokeOttoPortal({ action: 'la_connect', ...form }),
    onSuccess: () => {
      setForm({ companyId: '', username: '', password: '' })
      setEditing(false)
      toast({
        title: 'LimoAnywhere connected',
        description: 'The login was verified and Otto can now read your trips and quotes.',
      })
      queryClient.invalidateQueries({ queryKey: ['otto-la-status'] })
    },
    onError: (err: Error) =>
      toast({
        title: 'Could not connect LimoAnywhere',
        description:
          err.message === 'unavailable'
            ? 'The connection service is temporarily unavailable. Please try again in a minute.'
            : err.message,
        variant: 'destructive',
      }),
  })

  const disconnectMutation = useMutation({
    mutationFn: () => invokeOttoPortal({ action: 'la_disconnect' }),
    onSuccess: () => {
      toast({
        title: 'LimoAnywhere unlinked',
        description: 'The saved login was deleted. Otto can no longer read LimoAnywhere data.',
      })
      queryClient.invalidateQueries({ queryKey: ['otto-la-status'] })
    },
    onError: () =>
      toast({ title: 'Unlink failed', description: 'Please try again in a minute.', variant: 'destructive' }),
  })

  const status = statusQuery.data
  const needsReauth = status?.status === 'needs_reauth'
  const legacyLinked = status?.connected === true
  const showForm = !adminAccount && legacyLinked && (needsReauth || editing)
  const pluginConnected = plugin?.connected === true
  const canSubmit =
    form.companyId.trim() !== '' && form.username.trim() !== '' && form.password !== '' && !connectMutation.isPending

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>LimoAnywhere</CardTitle>
          {pluginQuery.data && (
            <Badge variant={pluginConnected ? 'default' : 'secondary'}>
              {pluginConnected ? (
                <Check className="mr-1 h-3 w-3" />
              ) : plugin ? (
                <TriangleAlert className="mr-1 h-3 w-3" />
              ) : (
                <Circle className="mr-1 h-3 w-3" />
              )}
              {pluginConnected ? 'Connected' : plugin ? 'Not seen recently' : 'Not connected'}
            </Badge>
          )}
        </div>
        <CardDescription>
          {needsReauth
            ? 'LimoAnywhere stopped accepting the saved login — the password probably changed. Enter it again below.'
            : 'Otto reads your trips, quotes and reservations through the Otto AI plugin in Claude, which keeps your LimoAnywhere login on your own computer. In Claude, run /otto-setup to connect it.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {plugin && (
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Plugin</span>
              <span className="font-medium">
                v{plugin.pluginVersion} on {plugin.host || plugin.platform}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last seen</span>
              <span className="font-medium">{relativeTime(plugin.lastSeenAt)}</span>
            </div>
            {!pluginConnected && (
              <p className="text-muted-foreground">
                No heartbeat in the last 7 days. Open Claude on that computer and ask Otto anything to refresh it.
              </p>
            )}
          </div>
        )}
        {statusQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : statusQuery.isError ? (
          <p className="text-sm text-muted-foreground">
            LimoAnywhere status is temporarily unavailable.{' '}
            <button className="underline" onClick={() => statusQuery.refetch()}>
              Retry
            </button>
          </p>
        ) : (
          <>
            {legacyLinked && (
              <div className="space-y-1 text-sm">
                <p className="text-xs text-muted-foreground">Legacy server-side login (no longer offered to new accounts)</p>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Company ID</span>
                  <span className="font-mono">{status.companyId}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Username</span>
                  <span className="font-medium">{status.username}</span>
                </div>
              </div>
            )}

            {showForm && (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (canSubmit) connectMutation.mutate()
                }}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="la-company">Company ID</Label>
                    <Input
                      id="la-company"
                      placeholder="e.g. 12345"
                      value={form.companyId}
                      onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="la-username">Username</Label>
                    <Input
                      id="la-username"
                      autoComplete="off"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="la-password">Password</Label>
                  <Input
                    id="la-password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    The same three fields as the manage.mylimobiz.com login. We verify the login with
                    LimoAnywhere, then store it encrypted — it never appears anywhere in Booked Rides.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={!canSubmit}>
                    {connectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {connectMutation.isPending
                      ? 'Verifying with LimoAnywhere…'
                      : needsReauth || status?.connected
                        ? 'Re-link LimoAnywhere'
                        : 'Connect LimoAnywhere'}
                  </Button>
                  {editing && (
                    <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            )}

            {!adminAccount && status?.connected && !showForm && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  Update login
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" disabled={disconnectMutation.isPending}>
                      {disconnectMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Unplug className="mr-2 h-4 w-4" />
                      )}
                      Unlink
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Unlink LimoAnywhere from Otto?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The saved login is deleted immediately and Otto loses access to your
                        LimoAnywhere data. You can reconnect anytime.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep connected</AlertDialogCancel>
                      <AlertDialogAction onClick={() => disconnectMutation.mutate()}>Unlink</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
